/* assets/gcitypartners-supplier-network.js — GCITYPARTNERS-SUPPLIER-NETWORK-03
 * Public Provider Directory stays anonymous/read-only.
 * Customer-scoped Project/RFQ/Quote/Award calls reuse LittleGWebSession native bearer.
 * Optional Project context records Supplier Search evidence through the existing Delivery event owner.
 */
(function (window, document) {
  'use strict';

  var VERSION = 'GCITYPARTNERS-SUPPLIER-NETWORK-03';
  var GLOBAL_KEY = '__GCITYPARTNERS_SUPPLIER_NETWORK__';
  var ROOT_SELECTOR = '[data-gcp-supplier-network]';
  var API_ORIGIN = 'https://api.glhksolution.com';
  var API_PREFIX = '/v1/gcitypartners/';
  var mounted = new WeakSet();
  var projectReadVersions = new WeakMap();
  var rfqReadVersions = new WeakMap();
  const projectReads = new Map();
  let projectEpoch = 0;

  if (window[GLOBAL_KEY] && typeof window[GLOBAL_KEY].mountAll === 'function') {
    window[GLOBAL_KEY].mountAll(document);
    return;
  }

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }
  function isZh() {
    return clean(document.documentElement.lang).toLowerCase().indexOf('zh') === 0;
  }
  /**
   * @param {string} zh
   * @param {string} en
   */
  function words(zh, en) {
    return isZh() ? zh : en;
  }
  // Use explicit simplified copy for locked Collaboration labels; never alter state or API enums.
  function collaborationWords(hant, hans, en) {
    var locale = clean(document.documentElement.lang).toLowerCase();
    return /^zh-(cn|hans|sg)(?:-|$)/.test(locale) ? hans : isZh() ? hant : en;
  }
  function list(value) {
    return Array.isArray(value) ? value : [];
  }
  function dataOf(payload) {
    return payload && typeof payload === 'object' ? payload : {};
  }
  function itemsOf(payload) {
    if (
      !payload ||
      !Array.isArray(payload.items) ||
      !payload.items.every(function (item) {
        return (
          item &&
          typeof item === 'object' &&
          !Array.isArray(item) &&
          typeof item.public_profile_id === 'string' &&
          item.public_profile_id.trim()
        );
      })
    )
      throw new Error('invalid_provider_response');
    return payload.items;
  }

  function endpoint(root, attribute) {
    var raw = clean(root.getAttribute(attribute));
    if (!raw) return '';
    try {
      var url = new URL(raw, API_ORIGIN);
      return url.origin === API_ORIGIN && url.pathname.indexOf(API_PREFIX) === 0
        ? url.toString()
        : '';
    } catch (_error) {
      return '';
    }
  }

  async function parseJsonResponse(response) {
    var payload = await response.json().catch(function () {
      return {};
    });
    if (!response.ok || !payload || payload.ok !== true) {
      var error = new Error(
        clean(payload && payload.error) ||
          clean(payload && payload.message) ||
          'request_' + response.status
      );
      error.status = response.status;
      error.serverMessage = clean(payload && payload.message);
      throw error;
    }
    return payload;
  }

  async function publicRequestJson(url) {
    if (!url) throw new Error('missing_endpoint');
    return parseJsonResponse(
      await window.fetch(url, {
        headers: { Accept: 'application/json' },
        credentials: 'omit',
      })
    );
  }

  function nativeSession() {
    var session = window.LittleGWebSession;
    return session && typeof session.authenticatedFetch === 'function' ? session : null;
  }

  async function authenticatedRequestJson(url, options) {
    if (!url) throw new Error('missing_endpoint');
    var session = nativeSession();
    if (!session) throw new Error('native_session_unavailable');
    var init = Object.assign({ headers: { Accept: 'application/json' } }, options || {});
    if (init.body && typeof init.body !== 'string') {
      init.body = JSON.stringify(init.body);
      init.headers = Object.assign({}, init.headers, { 'Content-Type': 'application/json' });
    }
    return parseJsonResponse(await session.authenticatedFetch(url, init));
  }

  function normalizeCapability(value) {
    return clean(value)
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9\u3400-\u9fff]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80);
  }

  function splitIds(value) {
    var seen = Object.create(null);
    // Presentation examples map back to the existing provider capability IDs.
    var examples = {
      測量與製圖: 'survey_mapping',
      估價: 'valuation',
      'surveying & mapping': 'survey_mapping',
    };
    return clean(value)
      .split(/[\n,、，]+/)
      .map(function (part) {
        var key = clean(part).toLowerCase();
        return normalizeCapability(examples[key] || part);
      })
      .filter(function (item) {
        if (!item || seen[item]) return false;
        seen[item] = true;
        return true;
      })
      .slice(0, 12);
  }

  function status(root, message, error) {
    var node = root.querySelector('[data-gcp-supplier-network-status]');
    root.setAttribute('data-gcp-supplier-network-state', error ? 'error' : 'ready');
    if (node) node.textContent = message || '';
  }

  function element(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = String(text);
    return node;
  }

  function minorDigits(currency) {
    var code = clean(currency).toUpperCase();
    if (['JPY', 'KRW'].indexOf(code) >= 0) return 0;
    if (['BHD', 'JOD', 'KWD', 'OMR', 'TND'].indexOf(code) >= 0) return 3;
    return 2;
  }

  function decimalToMinor(value, currency) {
    var raw = clean(value);
    if (!raw) return null;
    if (!/^\d+(?:\.\d+)?$/.test(raw)) throw new Error('invalid_amount');
    var digits = minorDigits(currency);
    var parts = raw.split('.');
    var whole = parts[0] || '0';
    var fraction = (parts[1] || '').slice(0, digits).padEnd(digits, '0');
    if ((parts[1] || '').length > digits && /[1-9]/.test((parts[1] || '').slice(digits)))
      throw new Error('amount_precision');
    var factor = Math.pow(10, digits);
    var minor = Number(whole) * factor + Number(fraction || 0);
    if (!Number.isSafeInteger(minor) || minor < 0) throw new Error('invalid_amount');
    return minor;
  }

  function formatMinor(currency, value) {
    var code = clean(currency).toUpperCase();
    var amount = Number(value);
    if (!code || !Number.isSafeInteger(amount)) return '—';
    var divisor = Math.pow(10, minorDigits(code));
    try {
      return new Intl.NumberFormat(isZh() ? 'zh-HK' : 'en-HK', {
        style: 'currency',
        currency: code,
        maximumFractionDigits: minorDigits(code),
      }).format(amount / divisor);
    } catch (_error) {
      return code + ' ' + String(amount / divisor);
    }
  }

  function isoFromLocal(value) {
    var raw = clean(value);
    if (!raw) return null;
    var date = new Date(raw);
    if (Number.isNaN(date.getTime()) || date.getTime() <= Date.now())
      throw new Error('invalid_due_at');
    return date.toISOString();
  }

  function formatDate(value) {
    var raw = clean(value);
    if (!raw) return '';
    var date = new Date(raw);
    if (Number.isNaN(date.getTime())) return raw;
    try {
      return new Intl.DateTimeFormat(isZh() ? 'zh-HK' : 'en-GB', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch (_error) {
      return raw;
    }
  }

  /* GCP-P602-RFQ-ELIGIBLE-01 | Only an actually listed parent project
   * can enable/submit RFQ. The backend remains the authorization owner.
   */
  function hasEligibleParentProject(form) {
    var select = form && form.elements && form.elements.job_id;
    var selectedId = clean(select && select.value);
    if (!selectedId || !select || !select.options) return false;
    return Array.prototype.some.call(select.options, function (option) {
      return (
        option.value === selectedId &&
        clean(option.getAttribute('data-gcp-project-relationship') || 'standalone') !==
          'subcontract'
      );
    });
  }

  function updateRfqSubmit(root) {
    var form = root.querySelector('[data-gcp-rfq-form]');
    var button = root.querySelector('[data-gcp-rfq-submit]');
    if (!form || !button) return;
    var provider = clean(
      form.elements.target_public_profile_id && form.elements.target_public_profile_id.value
    );
    button.disabled = !(provider && hasEligibleParentProject(form));
  }

  /* GCP-SUPPLIER-SPLIT-PREVIEW-01 — display-only; selection/RFQ owner is unchanged. */
  function resetSupplierPreview(root) {
    var preview = root.querySelector('[data-gcp-supplier-preview]');
    if (!preview) return;
    var empty = element('div', 'gcp-supplier-network__preview-empty');
    empty.appendChild(
      element('p', '', words('選擇合作夥伴，查看詳細資料。', 'Select a partner to view details.'))
    );
    preview.replaceChildren(empty);
  }

  function showSupplierPreview(root, card, item) {
    var preview = root.querySelector('[data-gcp-supplier-preview]');
    var results = root.querySelector('[data-gcp-supplier-results]');
    if (!preview || !results || !results.contains(card)) return;

    results.querySelectorAll('[data-gcp-supplier-active]').forEach(function (entry) {
      entry.removeAttribute('data-gcp-supplier-active');
    });
    results.querySelectorAll('[data-gcp-supplier-view]').forEach(function (button) {
      button.setAttribute('aria-pressed', 'false');
    });
    card.setAttribute('data-gcp-supplier-active', 'true');
    var view = card.querySelector('[data-gcp-supplier-view]');
    if (view) view.setAttribute('aria-pressed', 'true');

    // Reuse exactly the provider fields already rendered from the public API:
    // no second search request, fabricated qualifications, or private data.
    var details = card.cloneNode(true);
    details.removeAttribute('data-gcp-supplier-active');
    var oldActions = details.querySelector('.gcp-supplier-network__actions');
    if (oldActions) oldActions.remove();

    var actions = element('div', 'gcp-supplier-network__actions');
    var prepare = element(
      'button',
      '',
      words('選擇合作方並準備分包詢價', 'Select partner and prepare RFQ')
    );
    prepare.type = 'button';
    prepare.addEventListener('click', function () {
      selectSupplier(root, item);
    });
    actions.appendChild(prepare);
    preview.replaceChildren(details, actions);
  }

  function selectSupplier(root, item) {
    var form = root.querySelector('[data-gcp-rfq-form]');
    if (!form) return;
    form.elements.target_public_profile_id.value = clean(item.public_profile_id);
    form.elements.supplier_display.value = clean(item.display_name || item.legal_name);
    updateRfqSubmit(root);
    status(
      root,
      words(
        '已選擇合作方。請確認主專案及工作內容後再建立 RFQ。',
        'Partner selected. Confirm the parent project and work details before creating an RFQ.'
      )
    );
    var disclosure = form.closest('details');
    if (disclosure) disclosure.open = true;
    var dashboard = root.closest('[data-gcitypartners-dashboard]');
    var jump = dashboard && dashboard.querySelector('[data-gcp-rfq-jump]');
    if (jump && jump.isConnected) {
      jump.click();
      return;
    }
    var summary = disclosure && disclosure.querySelector('summary');
    if (summary) summary.focus({ preventScroll: true });
    var scrollBehavior =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth';
    try {
      form.scrollIntoView({ behavior: scrollBehavior, block: 'nearest' });
    } catch (_error) {}
  }

  function renderSuppliers(root, items) {
    var container = root.querySelector('[data-gcp-supplier-results]');
    if (!container) return;
    container.replaceChildren();
    if (!items.length) {
      container.appendChild(
        element(
          'p',
          'gcp-supplier-network__empty',
          words(
            '沒有符合目前條件的公開合作夥伴。',
            'No public supplier matches the current filters.'
          )
        )
      );
      return;
    }

    items.forEach(function (item) {
      const card = element('article', 'gcp-supplier-network__supplier');
      if (Array.isArray(item.capabilities)) {
        const capabilityIds = item.capabilities
          .map(function (capability) {
            return capability && typeof capability.id === 'string'
              ? normalizeCapability(capability.id)
              : '';
          })
          .filter(Boolean);

        card.setAttribute(
          'data-gcp-provider-capability-ids',
          JSON.stringify(Array.from(new Set(capabilityIds)))
        );
      }

      var head = element('div', 'gcp-supplier-network__supplier-head');
      var copy = document.createElement('div');
      copy.appendChild(
        element(
          'strong',
          '',
          clean(item.display_name || item.legal_name) || words('未命名合作夥伴', 'Unnamed supplier')
        )
      );
      var meta = element('div', 'gcp-supplier-network__meta');
      const supplierFields = [
        [words('地區', 'Region'), item.region],
        [words('公司規模', 'Company size'), item.company_size],
        [words('接案狀態', 'Availability'), item.availability_status],
      ];

      supplierFields.forEach(function (field) {
        const value = clean(field[1]);
        meta.appendChild(
          element('span', '', field[0] + ': ' + (value || words('未提供', 'Not provided')))
        );
      });

      copy.appendChild(meta);
      var verification = dataOf(item.verification);
      head.appendChild(copy);
      const verificationStatus = clean(verification.status);
      const verificationLabel =
        verificationStatus === 'verified'
          ? words('已核實（依來源記錄）', 'Verified (source record)')
          : verificationStatus === 'declared'
            ? words('合作夥伴申報（未核實）', 'Provider-declared (unverified)')
            : verificationStatus || words('未提供', 'Not provided');
      const verificationBadge = element(
        'span',
        'gcp-supplier-network__state',
        words('核實狀態', 'Verification') + ': ' + verificationLabel
      );

      verificationBadge.title = words(
        '此為返回資料中的狀態；核實範圍及有效期須另行確認。',
        'Status returned by the data source; verification scope and validity require separate confirmation.'
      );

      head.appendChild(verificationBadge);

      card.appendChild(head);

      if (clean(item.public_summary))
        card.appendChild(element('p', 'gcp-supplier-network__fine', clean(item.public_summary)));

      var commercialMeta = element('div', 'gcp-supplier-network__meta');
      var languages = list(item.service_languages).map(clean).filter(Boolean).slice(0, 12);
      if (languages.length) {
        commercialMeta.appendChild(
          element('span', '', words('服務語言 ', 'Service languages ') + languages.join(', '))
        );
      }
      var preferredCurrency = clean(item.preferred_currency).toUpperCase();
      var minimumProjectSize = item.minimum_project_size_minor;
      var minimumKnown =
        typeof minimumProjectSize === 'number' &&
        Number.isSafeInteger(minimumProjectSize) &&
        minimumProjectSize >= 0;
      if (/^[A-Z]{3}$/.test(preferredCurrency)) {
        commercialMeta.appendChild(
          element(
            'span',
            '',
            minimumKnown
              ? words('最低項目 ', 'Minimum project ') +
                  formatMinor(preferredCurrency, minimumProjectSize)
              : words('偏好幣別 ', 'Preferred currency ') + preferredCurrency
          )
        );
      }
      var responseHours = item.typical_response_time_hours;
      if (
        typeof responseHours === 'number' &&
        Number.isSafeInteger(responseHours) &&
        responseHours >= 0
      ) {
        commercialMeta.appendChild(
          element(
            'span',
            '',
            words('一般回覆 ', 'Typical response ') +
              String(responseHours) +
              words(' 小時', ' hours')
          )
        );
      }
      if (commercialMeta.childNodes.length) card.appendChild(commercialMeta);
      if (clean(item.service_description))
        card.appendChild(
          element('p', 'gcp-supplier-network__fine', clean(item.service_description))
        );

      var match = dataOf(item.match);
      var matchRow = element('div', 'gcp-supplier-network__match');
      [
        { value: match.coverage_percent, label: words('能力覆蓋 ', 'Capability coverage ') },
        {
          value: match.verified_coverage_percent,
          label: words('已核實覆蓋 ', 'Verified coverage '),
        },
      ].forEach(function (metric) {
        if (
          typeof metric.value !== 'number' ||
          !Number.isFinite(metric.value) ||
          metric.value < 0 ||
          metric.value > 100
        )
          return;
        matchRow.appendChild(element('span', '', metric.label + Math.round(metric.value) + '%'));
      });
      if (matchRow.childNodes.length) card.appendChild(matchRow);

      var tags = element('div', 'gcp-supplier-network__tags');
      list(item.capabilities)
        .slice(0, 12)
        .forEach(function (capability) {
          var tag = element('span', 'gcp-supplier-network__tag', clean(capability.id));
          tag.setAttribute(
            'data-verified',
            clean(capability.verification_status) === 'verified' ? 'true' : 'false'
          );
          const capabilityStatus = clean(capability.verification_status || 'declared');
          tag.title =
            capabilityStatus === 'verified'
              ? words('來源標示已核實能力', 'Source-reported verified capability')
              : capabilityStatus === 'declared'
                ? words('合作夥伴申報能力（未核實）', 'Provider-declared capability (unverified)')
                : capabilityStatus;
          tags.appendChild(tag);
        });
      if (tags.childNodes.length) card.appendChild(tags);

      var actions = element('div', 'gcp-supplier-network__actions');
      var view = element('button', '', words('查看詳情', 'View details'));
      view.type = 'button';
      view.setAttribute('data-gcp-supplier-view', '');
      view.setAttribute('aria-pressed', 'false');
      view.addEventListener('click', function () {
        showSupplierPreview(root, card, item);
      });
      actions.appendChild(view);
      var select = element('button', '', words('選擇合作方', 'Select partner'));
      select.type = 'button';
      select.setAttribute('data-primary', 'true');
      select.addEventListener('click', function () {
        selectSupplier(root, item);
      });
      actions.appendChild(select);

      if (clean(item.website) && /^https:\/\//i.test(clean(item.website))) {
        var link = element('a', 'gcp-dashboard__button', words('網站', 'Website'));
        link.href = clean(item.website);
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        actions.appendChild(link);
      }
      if (clean(item.portfolio_url) && /^https:\/\//i.test(clean(item.portfolio_url))) {
        var portfolio = element('a', 'gcp-dashboard__button', words('作品', 'Portfolio'));
        portfolio.href = clean(item.portfolio_url);
        portfolio.target = '_blank';
        portfolio.rel = 'noopener noreferrer';
        actions.appendChild(portfolio);
      }
      card.appendChild(actions);
      container.appendChild(card);
    });
  }

  async function recordSupplierSearchEvidence(root, form, filters) {
    var projectId = clean(form.elements.project_id && form.elements.project_id.value);
    if (!projectId) {
      root.setAttribute('data-gcp-supplier-search-evidence', 'not-requested');
      return false;
    }

    var evidenceUrl = endpoint(root, 'data-supplier-search-evidence-endpoint');
    if (!evidenceUrl || !nativeSession()) {
      root.setAttribute('data-gcp-supplier-search-evidence', 'unavailable');
      return false;
    }

    try {
      await authenticatedRequestJson(evidenceUrl, {
        method: 'POST',
        body: {
          job_id: projectId,
          capabilities: filters.capabilities,
          region: filters.region || null,
          availability: filters.availability || null,
        },
      });
      root.setAttribute('data-gcp-supplier-search-evidence', 'recorded');
      return true;
    } catch (_error) {
      root.setAttribute('data-gcp-supplier-search-evidence', 'unavailable');
      return false;
    }
  }

  async function searchSuppliers(root, form) {
    var container = root.querySelector('[data-gcp-supplier-results]');
    var button = form.querySelector('button[type="submit"]');
    if (form.getAttribute('aria-busy') === 'true') return;

    form.setAttribute('aria-busy', 'true');
    if (button) button.disabled = true;
    if (container) {
      container.setAttribute('aria-busy', 'true');
      container.replaceChildren(
        element('p', 'gcp-supplier-network__empty', words('正在搜尋…', 'Searching…'))
      );
    }
    resetSupplierPreview(root);
    status(root, words('正在搜尋合作夥伴…', 'Searching suppliers…'));

    try {
      var capabilities = splitIds(form.elements.capabilities && form.elements.capabilities.value);
      var region = clean(form.elements.region && form.elements.region.value);
      var availability = clean(form.elements.availability && form.elements.availability.value);
      var url = new URL(endpoint(root, 'data-providers-endpoint'));
      if (capabilities.length) url.searchParams.set('capabilities', capabilities.join(','));
      if (region) url.searchParams.set('region', region);
      if (availability) url.searchParams.set('availability', availability);
      url.searchParams.set('limit', '24');

      var result = await publicRequestJson(url.toString());
      var items = itemsOf(result);
      renderSuppliers(root, items);

      var totalKnown =
        typeof result.total === 'number' &&
        Number.isSafeInteger(result.total) &&
        result.total >= items.length;
      status(
        root,
        totalKnown
          ? words('符合條件共 ', 'Total matching suppliers: ') +
              String(result.total) +
              words(' 家；本次顯示 ', '. Showing ') +
              String(items.length) +
              words(' 家。', '.')
          : words('本次返回 ', 'Returned ') +
              String(items.length) +
              words(' 家公開合作夥伴；總數未提供。', ' public suppliers; total not provided.')
      );

      void recordSupplierSearchEvidence(root, form, {
        capabilities: capabilities,
        region: region,
        availability: availability,
      });
    } catch (_error) {
      if (container) {
        container.replaceChildren(
          element(
            'p',
            'gcp-supplier-network__empty',
            words('搜尋資料暫不可用，請重試。', 'Search data is unavailable. Please retry.')
          )
        );
      }
      status(
        root,
        words('暫時無法搜尋合作夥伴。', 'Supplier search is temporarily unavailable.'),
        true
      );
    } finally {
      form.setAttribute('aria-busy', 'false');
      if (container) container.setAttribute('aria-busy', 'false');
      if (button) button.disabled = false;
    }
  }

  function renderProjectCards(root, items, phase) {
    var dashboard = root.closest('[data-gcitypartners-dashboard]');
    var container = dashboard && dashboard.querySelector('[data-gcp-project-cards]');
    if (!container) return;
    container.replaceChildren();
    container.setAttribute('aria-busy', String(phase === 'loading'));

    if (phase !== 'ready') {
      container.appendChild(
        element(
          'p',
          '',
          phase === 'loading'
            ? words('正在載入項目…', 'Loading projects…')
            : words(
                '項目資料暫不可用，這不代表沒有項目。',
                'Project data is unavailable. This does not mean no projects exist.'
              )
        )
      );
      return;
    }
    if (!items.length) {
      container.appendChild(
        element('p', '', words('本次查詢未返回項目。', 'No projects returned by this request.'))
      );
      return;
    }

    items.forEach(function (item) {
      var card = element('article', 'gcp-project-card');
      var title =
        typeof item.title === 'string' && item.title.trim()
          ? item.title.trim()
          : words('專業項目', 'Professional project');
      card.appendChild(element('strong', '', title));
      card.appendChild(
        element('small', '', words('記錄：…', 'Reference: …') + clean(item.job_id).slice(-8))
      );
      var states = element('div', 'gcp-project-card__states');
      [
        { label: words('工作狀態：', 'Work status: '), value: item.status },
        { label: words('商業狀態：', 'Commercial status: '), value: item.commercial_status },
      ].forEach(function (entry) {
        var value = clean(entry.value);
        if (!value) return;
        var key = value.toLowerCase();
        var labels = {
          draft: words('草稿', 'Draft'),
          in_progress: words('進行中', 'In progress'),
          awarded: words('已獲項目', 'Awarded'),
          completed: words('已完成', 'Completed'),
          pending_review: words('待審核', 'Pending review'),
        };
        var known = Object.prototype.hasOwnProperty.call(labels, key);
        var badge = element('span', '', entry.label + (known ? labels[key] : value));
        badge.setAttribute('data-project-state', known ? key : 'unknown');
        states.appendChild(badge);
      });
      if (states.childNodes.length) card.appendChild(states);
      container.appendChild(card);
    });
  }

  function appendProjectOption(select, item) {
    var option = document.createElement('option');
    var relationship = clean(item.project_relationship || 'standalone');
    option.value = clean(item.job_id);
    option.setAttribute('data-gcp-project-relationship', relationship);
    option.textContent =
      [clean(item.product_handle) || 'Project', clean(item.commercial_status || item.status)]
        .filter(Boolean)
        .join(' · ') +
      ' · …' +
      clean(item.job_id).slice(-6);
    select.appendChild(option);
  }

  function renderProjects(root, items) {
    var rfqForm = root.querySelector('[data-gcp-rfq-form]');
    var searchForm = root.querySelector('[data-gcp-supplier-search-form]');
    if (!rfqForm) return 0;

    var eligible = list(items).filter(function (item) {
      return clean((item && item.project_relationship) || 'standalone') !== 'subcontract';
    });

    var rfqSelect = rfqForm.elements.job_id;
    rfqSelect.replaceChildren();
    var rfqPlaceholder = document.createElement('option');
    rfqPlaceholder.value = '';
    rfqPlaceholder.textContent = eligible.length
      ? words('選擇 Professional Project', 'Select a Professional Project')
      : words('沒有可用 Professional Project', 'No eligible Professional Project');
    rfqSelect.appendChild(rfqPlaceholder);
    eligible.forEach(function (item) {
      appendProjectOption(rfqSelect, item);
    });

    var searchSelect = searchForm && searchForm.elements.project_id;
    if (searchSelect) {
      searchSelect.replaceChildren();
      var publicOption = document.createElement('option');
      publicOption.value = '';
      publicOption.textContent = words(
        '公開搜尋（不綁 Project）',
        'Public search (no Project evidence)'
      );
      searchSelect.appendChild(publicOption);
      eligible.forEach(function (item) {
        appendProjectOption(searchSelect, item);
      });
    }

    var empty = rfqForm.querySelector('[data-gcp-project-empty]');
    var action = empty && empty.querySelector('[data-gcp-project-empty-action]');
    var dashboard = root.closest('[data-gcitypartners-dashboard]');
    var briefTrigger = dashboard && dashboard.querySelector('[data-gcp-brief-open]');
    if (empty) empty.hidden = eligible.length !== 0;
    if (action) {
      action.disabled = !briefTrigger;
      action.onclick = function () {
        if (!briefTrigger || !briefTrigger.isConnected) return;
        var panel = root.querySelector('[data-gcp-rfq-dialog]');
        if (panel && panel.open) panel.close();
        window.setTimeout(function () {
          if (briefTrigger.isConnected) briefTrigger.click();
        }, 0);
      };
    }

    renderProjectCards(root, list(items), 'ready');
    updateRfqSubmit(root);
    return eligible.length;
  }

  function projectSubject(session) {
    const current = session && typeof session.getState === 'function' ? session.getState() : null;

    return current && current.status === 'authenticated'
      ? clean(current.profile && current.profile.id)
      : '';
  }

  function projectScopeError() {
    const error = new Error('project_scope_changed');
    error.code = 'project_scope_changed';
    return error;
  }

  function invalidateProjectReads() {
    projectEpoch += 1;

    projectReads.forEach(function (record) {
      record.controller.abort();
    });

    projectReads.clear();
  }

  async function readProjectPage(rawUrl) {
    const session = nativeSession();

    if (!session || typeof session.getState !== 'function') {
      const error = new Error('native_session_unavailable');
      error.status = 401;
      throw error;
    }

    if (typeof session.ready === 'function') {
      await session.ready();
    }

    const subject = projectSubject(session);
    if (!subject) {
      const error = new Error('authentication_required');
      error.status = 401;
      throw error;
    }

    const url = new URL(rawUrl, API_ORIGIN);
    if (
      url.origin !== API_ORIGIN ||
      url.pathname !== '/v1/gcitypartners/commercial/projects' ||
      url.username ||
      url.password ||
      url.hash
    ) {
      throw new Error('invalid_project_endpoint');
    }

    const epoch = projectEpoch;
    const key = subject + '\n' + url.href;
    const currentScope = function () {
      return (
        projectEpoch === epoch && nativeSession() === session && projectSubject(session) === subject
      );
    };

    let record = projectReads.get(key);

    if (!record || record.expiresAt <= Date.now()) {
      const controller = new AbortController();
      record = {
        controller: controller,
        expiresAt: Infinity,
        promise: null,
      };

      record.promise = (async function () {
        const timeout = window.setTimeout(function () {
          controller.abort();
        }, 25000);

        const payload = await authenticatedRequestJson(url.href, {
          signal: controller.signal,
        }).finally(function () {
          window.clearTimeout(timeout);
        });

        if (!currentScope()) throw projectScopeError();

        if (
          !Array.isArray(payload.items) ||
          !payload.items.every(function (item) {
            return (
              item &&
              typeof item === 'object' &&
              !Array.isArray(item) &&
              typeof item.job_id === 'string' &&
              item.job_id.trim().length > 0
            );
          })
        ) {
          throw new Error('invalid_project_response');
        }

        record.expiresAt = Date.now() + 15000;

        return {
          items: payload.items,
          total_count:
            Number.isSafeInteger(payload.total_count) && payload.total_count >= payload.items.length
              ? payload.total_count
              : null,
          subject: subject,
          epoch: epoch,
        };
      })().catch(function (error) {
        if (projectReads.get(key) === record) {
          projectReads.delete(key);
        }

        if (!currentScope()) throw projectScopeError();
        throw error;
      });

      projectReads.set(key, record);
    }

    const page = await record.promise;
    if (!currentScope()) throw projectScopeError();

    return JSON.parse(JSON.stringify(page));
  }

  function publishProjectSummary(root, state, items, requestId) {
    const dashboard = root.closest('[data-gcitypartners-dashboard]');
    if (!dashboard) return;

    const presentation = window.GCityPartnersOverviewPresentation;
    if (presentation && typeof presentation.mount === 'function') {
      presentation.mount(dashboard);
    }

    const summary = dashboard.querySelector('[data-gcp-overview-module="projects"]');
    if (!summary) return;

    if (!requestId) {
      summary.dispatchEvent(new CustomEvent('gcp:overview-reset'));
    }

    const detail = {
      state: state,
      request_id: requestId || '',
    };

    if (state === 'ready' || state === 'empty') {
      detail.confirmed = true;
      detail.items = list(items)
        .slice(0, 5)
        .map(function (item) {
          return {
            id: clean(item.job_id),
            title: clean(item.title) || words('專業項目', 'Professional project'),
            status: clean(item.status),
            updated_at: typeof item.updated_at === 'string' ? item.updated_at : '',
            relationships: Array.isArray(item.relationships)
              ? item.relationships.filter(function (value) {
                  return ['commissioned', 'responsible', 'participant'].includes(value);
                })
              : [],
          };
        });
    }

    summary.dispatchEvent(
      new CustomEvent('gcp:overview-state', {
        detail: detail,
      })
    );
  }

  async function loadProjects(root, requestId) {
    var version = (projectReadVersions.get(root) || 0) + 1;
    projectReadVersions.set(root, version);
    renderProjects(root, []);
    renderProjectCards(root, [], 'loading');
    publishProjectSummary(root, 'loading', [], requestId);

    var rfqForm = root.querySelector('[data-gcp-rfq-form]');
    var empty = rfqForm && rfqForm.querySelector('[data-gcp-project-empty]');
    if (empty) empty.hidden = true;

    const projectSelect = rfqForm && rfqForm.elements.job_id;
    if (projectSelect && projectSelect.options.length) {
      projectSelect.options[0].textContent = words('正在載入主專案…', 'Loading parent projects…');
    }

    const searchForm = root.querySelector('[data-gcp-supplier-search-form]');
    const searchProject = searchForm && searchForm.elements.project_id;
    if (searchProject && searchProject.options.length) {
      searchProject.options[0].textContent = words(
        '公開搜尋（專案載入中）',
        'Public search (projects loading)'
      );
    }

    try {
      const payload = await readProjectPage(endpoint(root, 'data-projects-endpoint') + '?limit=50');

      if (
        projectReadVersions.get(root) !== version ||
        !root.isConnected ||
        payload.epoch !== projectEpoch ||
        payload.subject !== projectSubject(nativeSession())
      )
        return;

      const count = renderProjects(root, payload.items);

      publishProjectSummary(
        root,
        payload.items.length ? 'ready' : 'empty',
        payload.items,
        requestId
      );

      if (!count)
        status(
          root,
          collaborationWords(
            '發出分包詢價前，請先建立正式項目。',
            '发出分包询价前，请先建立正式项目。',
            'You need an existing project to send an RFQ.'
          )
        );
    } catch (error) {
      if (
        projectReadVersions.get(root) !== version ||
        !root.isConnected ||
        (error && error.code === 'project_scope_changed')
      )
        return;

      renderProjects(root, []);
      renderProjectCards(root, [], 'error');

      publishProjectSummary(
        root,
        error && (error.status === 401 || error.status === 403) ? 'unauthorized' : 'fetch_failed',
        [],
        requestId
      );

      if (empty) empty.hidden = true;
      var select = rfqForm && rfqForm.elements.job_id;
      if (select && select.options.length)
        select.options[0].textContent = words('項目暫時無法載入', 'Projects could not be loaded');
      status(
        root,
        error && error.status === 401
          ? words(
              '登入後可載入你的 Professional Projects。',
              'Sign in to load your Professional Projects.'
            )
          : words(
              '暫時無法載入項目；這不代表沒有項目。',
              'Projects could not be loaded. This does not mean no projects exist.'
            ),
        true
      );
    }
  }

  function createIdempotencyKey() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function')
      return 'gcp-rfq-' + window.crypto.randomUUID();
    return 'gcp-rfq-' + Date.now() + '-' + Math.random().toString(36).slice(2);
  }

  async function createRfq(root, form) {
    if (form.getAttribute('aria-busy') === 'true') return;
    var currency = clean(form.elements.currency.value).toUpperCase();
    var body;
    try {
      body = {
        job_id: clean(form.elements.job_id.value),
        target_public_profile_id: clean(form.elements.target_public_profile_id.value),
        title: clean(form.elements.title.value),
        scope: clean(form.elements.scope.value) || null,
        currency: currency,
        budget_min_minor: decimalToMinor(form.elements.budget_min.value, currency),
        budget_max_minor: decimalToMinor(form.elements.budget_max.value, currency),
        due_at: isoFromLocal(form.elements.due_at.value),
      };
    } catch (error) {
      var inputCode = clean(error && error.message);
      status(
        root,
        inputCode === 'amount_precision'
          ? words('金額小數位超出幣別精度。', 'Amount precision exceeds the currency precision.')
          : inputCode === 'invalid_due_at'
            ? words('RFQ 截止時間必須晚於現在。', 'RFQ deadline must be in the future.')
            : words('請檢查金額或截止時間。', 'Check the amount or deadline.'),
        true
      );
      return;
    }

    if (!body.job_id || !body.target_public_profile_id || !body.title) {
      status(
        root,
        words(
          '請先選擇合作夥伴、Project 並填寫 RFQ 標題。',
          'Select a supplier and project, then add an RFQ title.'
        ),
        true
      );
      return;
    }
    if (!hasEligibleParentProject(form) || !projectSubject(nativeSession())) {
      status(
        root,
        words(
          '請先以已登入帳戶選擇合資格的主項目，再建立 RFQ。',
          'Sign in and select an eligible parent project before creating an RFQ.'
        ),
        true
      );
      return;
    }
    if (
      body.budget_min_minor !== null &&
      body.budget_max_minor !== null &&
      body.budget_min_minor > body.budget_max_minor
    ) {
      status(
        root,
        words('預算下限不能高於預算上限。', 'Budget minimum cannot exceed budget maximum.'),
        true
      );
      return;
    }

    var button = form.querySelector('[data-gcp-rfq-submit]');
    form.setAttribute('aria-busy', 'true');
    if (button) button.disabled = true;
    status(root, words('正在建立 RFQ…', 'Creating RFQ…'));
    try {
      await authenticatedRequestJson(endpoint(root, 'data-rfq-create-endpoint'), {
        method: 'POST',
        headers: { 'Idempotency-Key': createIdempotencyKey() },
        body: body,
      });
      status(
        root,
        words(
          'RFQ 已建立。合作夥伴可在 Provider Portal 查看並報價。',
          'RFQ created. The supplier can review and quote it in the Provider Portal.'
        )
      );
      form.elements.title.value = '';
      form.elements.scope.value = '';
      form.elements.budget_min.value = '';
      form.elements.budget_max.value = '';
      form.elements.due_at.value = '';
      await loadRfqs(root);
    } catch (error) {
      var code = clean(error && error.message);
      var message =
        code === 'invalid_due_at'
          ? words('RFQ 截止時間必須晚於現在。', 'RFQ deadline must be in the future.')
          : code === 'target_provider_not_found' || code === 'target_provider_not_approved'
            ? words(
                '該合作夥伴目前不可接受新的公開 RFQ。',
                'That supplier is not currently available for a new public RFQ.'
              )
            : code === 'project_not_rfq_eligible'
              ? words(
                  '此 Project 目前不能建立新的 RFQ。',
                  'This Project is not currently eligible for a new RFQ.'
                )
              : words('RFQ 暫時無法建立。', 'RFQ could not be created.');
      status(root, message, true);
    } finally {
      form.setAttribute('aria-busy', 'false');
      updateRfqSubmit(root);
    }
  }

  async function awardQuote(root, quoteId, button) {
    if (
      !quoteId ||
      !button ||
      button.disabled ||
      button.getAttribute('aria-busy') === 'true' ||
      button.getAttribute('data-award-accepted') === 'true'
    )
      return;
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    status(root, words('正在確認 Award…', 'Confirming award…'));

    try {
      await authenticatedRequestJson(endpoint(root, 'data-award-endpoint'), {
        method: 'POST',
        body: { quote_id: quoteId },
      });
      button.setAttribute('data-award-accepted', 'true');
      var refreshed = await loadRfqs(root);
      status(
        root,
        refreshed
          ? words(
              '伺服器已接受 Award 請求，RFQ 列表已刷新。請核對交易狀態；已獲項目值不代表已收款或利潤。',
              'The server accepted the Award request and the RFQ list refreshed. Check the transaction status; awarded value is not cash received or profit.'
            )
          : words(
              '伺服器已接受 Award 請求，但列表刷新失敗。請重新載入核對，勿重複提交。',
              'The server accepted the Award request, but the list could not refresh. Reload to check; do not submit again.'
            ),
        !refreshed
      );
    } catch (error) {
      var code = clean(error && error.message);
      status(
        root,
        code === 'quote_expired'
          ? words(
              '此 Quote 已過有效期，不能 Award。',
              'This quote has expired and cannot be awarded.'
            )
          : words(
              '未能確認 Award 結果。請先重新載入核對交易記錄，勿立即重複提交。',
              'The Award result could not be confirmed. Reload and check transaction records before submitting again.'
            ),
        true
      );
      // An Award may already be recorded after a network error; require fresh RFQ readback before another attempt.
    } finally {
      button.setAttribute('aria-busy', 'false');
    }
  }

  async function loadQuotes(root, rfqId, container) {
    if (container.getAttribute('aria-busy') === 'true') return;
    var session = nativeSession();
    var initial = session && typeof session.getState === 'function' ? session.getState() : null;
    var subject = clean(initial && initial.profile && initial.profile.id);
    if (!initial || initial.status !== 'authenticated' || !subject) {
      container.replaceChildren();
      return;
    }
    var version = rfqReadVersions.get(root);
    function isCurrent() {
      var active = session.getState();
      return (
        root.isConnected &&
        container.isConnected &&
        nativeSession() === session &&
        rfqReadVersions.get(root) === version &&
        active &&
        active.status === 'authenticated' &&
        clean(active.profile && active.profile.id) === subject
      );
    }
    container.setAttribute('aria-busy', 'true');
    container.replaceChildren(
      element('p', 'gcp-supplier-network__empty', words('正在載入 Quote…', 'Loading quotes…'))
    );

    try {
      if (!rfqId) throw new Error('missing_rfq_id');
      var url = new URL(endpoint(root, 'data-quotes-endpoint'));
      url.searchParams.set('rfq_id', rfqId);
      var payload = await authenticatedRequestJson(url.toString());
      if (!isCurrent()) return;
      if (
        !Array.isArray(payload.items) ||
        !payload.items.every(function (quote) {
          return quote && typeof quote === 'object' && !Array.isArray(quote);
        })
      )
        throw new Error('invalid_quote_response');

      var items = payload.items;
      container.replaceChildren();
      if (!items.length) {
        container.appendChild(
          element(
            'p',
            'gcp-supplier-network__empty',
            words('本次查詢未返回 Quote。', 'No quotes returned by this request.')
          )
        );
        return;
      }

      items.forEach(function (quote) {
        var quoteId = clean(quote.quote_id);
        var quoteStatus = clean(quote.status);
        var currency = clean(quote.currency).toUpperCase();
        var validAmount =
          /^[A-Z]{3}$/.test(currency) &&
          typeof quote.amount_minor === 'number' &&
          Number.isSafeInteger(quote.amount_minor) &&
          quote.amount_minor > 0;
        var card = element('article', 'gcp-supplier-network__quote');
        card.setAttribute('data-status', quoteStatus || 'unknown');

        var head = element('div', 'gcp-supplier-network__quote-head');
        head.appendChild(
          element('strong', '', validAmount ? formatMinor(currency, quote.amount_minor) : '—')
        );
        head.appendChild(
          element(
            'span',
            'gcp-supplier-network__state',
            quoteStatus || words('狀態待確認', 'Status unavailable')
          )
        );
        card.appendChild(head);

        var meta = element('div', 'gcp-supplier-network__meta');
        if (
          typeof quote.revision === 'number' &&
          Number.isSafeInteger(quote.revision) &&
          quote.revision > 0
        ) {
          meta.appendChild(
            element('span', '', words('版本 ', 'Revision ') + String(quote.revision))
          );
        }
        if (clean(quote.valid_until))
          meta.appendChild(
            element('span', '', words('有效至 ', 'Valid until ') + formatDate(quote.valid_until))
          );
        if (meta.childNodes.length) card.appendChild(meta);
        if (clean(quote.note))
          card.appendChild(element('p', 'gcp-supplier-network__fine', clean(quote.note)));

        if (quoteStatus === 'submitted' && quoteId) {
          var actions = element('div', 'gcp-supplier-network__actions');
          var award = element('button', '', words('Award 此 Quote', 'Award this quote'));
          award.type = 'button';
          award.setAttribute('data-primary', 'true');
          award.addEventListener('click', function () {
            awardQuote(root, quoteId, award);
          });
          actions.appendChild(award);
          card.appendChild(actions);
        }
        container.appendChild(card);
      });
    } catch (error) {
      if (!isCurrent()) return;
      if (error && (error.status === 401 || error.status === 403)) {
        var rfqs = root.querySelector('[data-gcp-customer-rfqs]');
        if (rfqs)
          rfqs.replaceChildren(
            element(
              'p',
              'gcp-supplier-network__empty',
              words(
                '無法存取 RFQ 或 Quote，私人資料已清除。',
                'RFQ or Quote access denied. Private records have been cleared.'
              )
            )
          );
        status(root, words('RFQ／Quote 存取權限不足。', 'RFQ/Quote access denied.'), true);
      } else {
        container.replaceChildren(
          element(
            'p',
            'gcp-supplier-network__empty',
            words('暫時無法載入 Quote。', 'Quotes are temporarily unavailable.')
          )
        );
      }
    } finally {
      if (isCurrent()) container.setAttribute('aria-busy', 'false');
    }
  }

  function renderRfqs(root, items) {
    var container = root.querySelector('[data-gcp-customer-rfqs]');
    if (!container) return;
    container.replaceChildren();

    if (!items.length) {
      var template = root.querySelector('[data-gcp-rfq-empty-template]');
      if (!template || !template.content) {
        container.appendChild(
          element(
            'p',
            'gcp-supplier-network__empty',
            collaborationWords('目前沒有詢價', '目前没有询价', 'No RFQs Yet')
          )
        );
        return;
      }
      container.appendChild(template.content.cloneNode(true));
      var action = container.querySelector('[data-gcp-rfq-empty-action]');
      var dashboard = root.closest('[data-gcitypartners-dashboard]');
      var trigger = dashboard && dashboard.querySelector('[data-gcp-rfq-jump]');
      if (action) {
        action.disabled = !trigger;
        action.onclick = function () {
          if (trigger && trigger.isConnected) trigger.click();
        };
      }
      return;
    }

    items.forEach(function (rfq) {
      var card = element('article', 'gcp-supplier-network__rfq');
      var head = element('div', 'gcp-supplier-network__rfq-head');
      var copy = document.createElement('div');
      copy.appendChild(element('strong', '', clean(rfq.title) || 'RFQ'));
      var supplier = dataOf(rfq.supplier);
      var meta = element('div', 'gcp-supplier-network__meta');
      if (clean(supplier.display_name))
        meta.appendChild(element('span', '', clean(supplier.display_name)));
      meta.appendChild(
        element('span', '', clean(rfq.status) || words('狀態待確認', 'Status unavailable'))
      );
      if (clean(rfq.due_at))
        meta.appendChild(element('span', '', words('截止 ', 'Due ') + formatDate(rfq.due_at)));
      copy.appendChild(meta);
      head.appendChild(copy);

      var currency = clean(rfq.currency).toUpperCase();
      var validCurrency = /^[A-Z]{3}$/.test(currency);
      var min = rfq.budget_min_minor;
      var max = rfq.budget_max_minor;
      var minKnown = typeof min === 'number' && Number.isSafeInteger(min) && min >= 0;
      var maxKnown = typeof max === 'number' && Number.isSafeInteger(max) && max >= 0;
      var budget = words('預算未提供或資料待確認', 'Budget unavailable');
      if (validCurrency && minKnown && maxKnown && min <= max)
        budget =
          words('預算 ', 'Budget ') +
          formatMinor(currency, min) +
          ' – ' +
          formatMinor(currency, max);
      else if (validCurrency && minKnown && !maxKnown)
        budget = words('預算下限 ', 'Budget minimum ') + formatMinor(currency, min);
      else if (validCurrency && maxKnown && !minKnown)
        budget = words('預算上限 ', 'Budget maximum ') + formatMinor(currency, max);
      head.appendChild(element('span', 'gcp-supplier-network__state', budget));
      card.appendChild(head);

      var actions = element('div', 'gcp-supplier-network__actions');
      var quotesButton = element('button', '', words('查看 Quote', 'View quotes'));
      quotesButton.type = 'button';
      quotesButton.setAttribute('aria-expanded', 'false');
      if (!clean(rfq.rfq_id)) {
        quotesButton.disabled = true;
        quotesButton.textContent = words('RFQ 資料待確認', 'RFQ reference unavailable');
      }
      var quotes = element('div', 'gcp-supplier-network__quotes');
      quotes.hidden = true;
      quotes.setAttribute('aria-busy', 'false');
      quotesButton.addEventListener('click', function () {
        quotes.hidden = !quotes.hidden;
        quotesButton.setAttribute('aria-expanded', String(!quotes.hidden));
        quotesButton.textContent = quotes.hidden
          ? words('查看 Quote', 'View quotes')
          : words('收起 Quote', 'Hide quotes');
        if (!quotes.hidden) void loadQuotes(root, clean(rfq.rfq_id), quotes);
      });
      actions.appendChild(quotesButton);
      card.appendChild(actions);
      card.appendChild(quotes);
      container.appendChild(card);
    });
  }

  async function loadRfqs(root) {
    var version = (rfqReadVersions.get(root) || 0) + 1;
    rfqReadVersions.set(root, version);
    var container = root.querySelector('[data-gcp-customer-rfqs]');
    var owner = nativeSession();
    var current = owner && typeof owner.getState === 'function' ? owner.getState() : null;
    var principalId = clean(current && current.profile && current.profile.id);

    if (container) {
      container.setAttribute('aria-busy', 'true');
      container.replaceChildren();
    }
    if (!current || current.status !== 'authenticated' || !principalId) {
      if (container) {
        container.setAttribute('aria-busy', 'false');
        container.appendChild(
          element(
            'p',
            'gcp-supplier-network__empty',
            words('登入後可查看你的 RFQ。', 'Sign in to view your RFQs.')
          )
        );
      }
      return false;
    }
    if (container)
      container.appendChild(
        element('p', 'gcp-supplier-network__empty', words('正在載入 RFQ…', 'Loading RFQs…'))
      );

    function isCurrent() {
      if (!root.isConnected || rfqReadVersions.get(root) !== version || nativeSession() !== owner)
        return false;
      var active = owner.getState();
      return (
        active &&
        active.status === 'authenticated' &&
        clean(active.profile && active.profile.id) === principalId
      );
    }

    try {
      var payload = await authenticatedRequestJson(
        endpoint(root, 'data-rfqs-endpoint') + '?limit=50'
      );
      if (!isCurrent()) return false;
      if (
        !Array.isArray(payload.items) ||
        !payload.items.every(function (item) {
          return item && typeof item === 'object' && !Array.isArray(item);
        })
      )
        throw new Error('invalid_rfq_response');
      renderRfqs(root, payload.items);
      return true;
    } catch (error) {
      if (!isCurrent()) return false;
      if (container)
        container.replaceChildren(
          element(
            'p',
            'gcp-supplier-network__empty',
            words(
              'RFQ 記錄暫不可用；這不代表沒有交易記錄。',
              'RFQ records are unavailable. This does not mean no transaction records exist.'
            )
          )
        );
      status(
        root,
        error && error.status === 401
          ? words(
              '登入狀態已失效，請重新登入後核對 RFQ。',
              'Your sign-in has expired. Sign in again to check RFQs.'
            )
          : error && error.status === 403
            ? words(
                '沒有權限查看此 RFQ，相關私人資料已清除。',
                'Access denied. Private RFQ details have been cleared.'
              )
            : words('暫時無法載入 RFQ。', 'RFQs are temporarily unavailable.'),
        true
      );
      return false;
    } finally {
      if (container && rfqReadVersions.get(root) === version)
        container.setAttribute('aria-busy', 'false');
    }
  }

  function refreshPrivate(root) {
    void loadProjects(root);
    void loadRfqs(root);
  }

  document.addEventListener('gcp:delivery-saved', function (event) {
    const target = event.target;
    const detail = event.detail;

    if (
      !target ||
      typeof target.matches !== 'function' ||
      !target.matches('[data-gcp-delivery-lifecycle]') ||
      target.getAttribute('data-gcp-delivery-mode') !== 'buyer' ||
      !detail ||
      !clean(detail.job_id) ||
      !clean(detail.subject) ||
      clean(detail.subject) !== projectSubject(nativeSession())
    )
      return;

    const dashboard = target.closest('[data-gcitypartners-dashboard]');
    const root = dashboard && dashboard.querySelector(ROOT_SELECTOR);
    if (!root || !root.isConnected) return;

    invalidateProjectReads();
    void loadProjects(root);
  });

  document.addEventListener('gcp:overview-request', function (event) {
    const detail = event.detail;
    const target = event.target;

    if (
      !detail ||
      detail.module !== 'projects' ||
      !clean(detail.request_id) ||
      !target ||
      typeof target.matches !== 'function' ||
      !target.matches('[data-gcp-overview-module="projects"]')
    )
      return;

    const dashboard = target.closest('[data-gcitypartners-dashboard]');
    const root = dashboard && dashboard.querySelector(ROOT_SELECTOR);
    if (!root || !root.isConnected) return;

    event.preventDefault();
    void loadProjects(root, clean(detail.request_id));
  });

  function mount(root) {
    if (!root || mounted.has(root)) return;
    mounted.add(root);
    var searchForm = root.querySelector('[data-gcp-supplier-search-form]');
    var rfqForm = root.querySelector('[data-gcp-rfq-form]');
    var refresh = root.querySelector('[data-gcp-rfq-refresh]');

    if (searchForm) {
      searchForm.addEventListener('submit', function (event) {
        event.preventDefault();
        void searchSuppliers(root, searchForm);
      });
      if (searchForm.elements.project_id) {
        searchForm.elements.project_id.addEventListener('change', function () {
          var projectId = clean(searchForm.elements.project_id.value);
          if (!projectId || !rfqForm || !rfqForm.elements.job_id) return;
          var option = Array.prototype.find.call(rfqForm.elements.job_id.options, function (item) {
            return item.value === projectId;
          });
          if (option) {
            rfqForm.elements.job_id.value = projectId;
            updateRfqSubmit(root);
          }
        });
      }
    }

    if (rfqForm) {
      rfqForm.addEventListener('submit', function (event) {
        event.preventDefault();
        void createRfq(root, rfqForm);
      });
      if (rfqForm.elements.job_id)
        rfqForm.elements.job_id.addEventListener('change', function () {
          updateRfqSubmit(root);
        });
    }
    if (refresh)
      refresh.addEventListener('click', function () {
        void loadRfqs(root);
      });
    refreshPrivate(root);
  }

  function mountAll(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll(ROOT_SELECTOR), mount);
  }

  function refreshAll(scope) {
    Array.prototype.forEach.call(
      (scope || document).querySelectorAll(ROOT_SELECTOR),
      function (root) {
        if (mounted.has(root)) refreshPrivate(root);
        else mount(root);
      }
    );
  }

  var api = Object.freeze({
    version: VERSION,
    mountAll: mountAll,
    splitIds: splitIds,
    normalizeCapability: normalizeCapability,
    decimalToMinor: decimalToMinor,
    minorDigits: minorDigits,
  });
  window[GLOBAL_KEY] = api;
  window.GCityPartnersSupplierNetwork = api;
  window.GCityPartnersProjects = Object.freeze({
    readPage: readProjectPage,
    invalidate: invalidateProjectReads,
  });
  document.addEventListener('littleg:web-session-change', function () {
    invalidateProjectReads();
    refreshAll(document);
  });
  document.addEventListener('shopify:section:load', function (event) {
    mountAll(event.target || document);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    const scope = event.target;
    if (!scope || typeof scope.querySelector !== 'function') return;

    if (
      (typeof scope.matches === 'function' && scope.matches(ROOT_SELECTOR)) ||
      scope.querySelector(ROOT_SELECTOR)
    ) {
      invalidateProjectReads();
    }
  });
  if (document.readyState === 'loading')
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        mountAll(document);
      },
      { once: true }
    );
  else mountAll(document);
})(window, document);
